/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { discountCardMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/discountCardMutationKeys";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useContext, lazy, useCallback, useMemo, useRef } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  StyledCreateButton,
  StyledTitle,
} from "../user_management/styles";
import AddIcon from "@mui/icons-material/Add";
import {
  DiscountCardContentContainer,
  DiscountCardNameContainer,
  EditDiscountCardDialog,
  EditDiscountCardDialogContent,
  EditDiscountCardDialogHeader,
  EditDiscountCardDialogTitle,
  ModalContainer,
  StyledBreadCrumbContainer,
  StyledHeadingContainer,
  StyledLink,
  StyledTitleContainer,
  StyledTypography,
  LoaderContainer,
} from "./styles";
import "@fontsource/source-sans-pro";
import api from "../../api/index.js";
import authenticatedFetch from "../../api/authenticatedFetch";
import { Loader } from "../../components/loader";
import { useNotificationContext } from "../../contexts/notificationContext";
import CheckIcon from "@mui/icons-material/Check";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import CloseIcon from "@mui/icons-material/Close";
import { IndexTable } from "../../components/index_table";
import camelize from "../../utils/camelize.js";
import { DiscountCardName } from "../../components/discount_card/discount_card_name";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TranslateIcon from "@mui/icons-material/Translate";
import { AbilityContext } from "../../contexts/abilityContext";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { mapLanguage } from "../../utils/translate.js";
import { selectBaseStyles, viewTypographyStyles } from "../reports/styles";
import { UpdateButton } from "../../components/update_button";
import { FilterContainer } from "../../components/filter_container";
import { StyledInputLabel } from "../restaurant/styles.jsx";
import { useInfiniteQuery, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { strictCanScope } from "../../utils/caslStrict.js";
import LanguageSelector from "../../components/language_selector/index.jsx";
import { updateAdminLanguage } from "../../store/adminSlice.js";
import DropdownSearchBar from "../../components/dorpdown_searchbar/index.jsx";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { formatDate } from '../../utils/formatDate.js'
import { SelectedMenuItem } from '../../components/selected_menu_item/index.jsx'
import { DiscountCardTranslationModal } from '../../components/discount_card_translation_modal/index.jsx'
import DiscountCardGlobalDetailsView from "../../components/discount_card_global_details";
import DiscountCardVariantGroupsView from "../../components/discount_card_variant_groups/view";
import { buildQueryParams } from "../../utils/buildQueryParams.js";
import dayjs from "dayjs";
import { StyledMenuItem } from "../../components/styled_menu_item/index.jsx";
import { Query_Keys } from "../../lib/queryKeys.js";
import { lazyWithRetry } from "../../utils/lazyWithRetry.js";
import { PerPage } from "../../constants/perPage.js";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery.js";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { useDebounce } from "../../utils/useDebounce.js";
import { resetPageAfterDeletion } from '../../utils/resetPageAfterDeletion'
import { CARD_SOURCE_ALL, CARD_SOURCE_OPTIONS, cardSourceParams } from "../../components/card_source_filter";

const DiscountCardBuilder = lazy(lazyWithRetry(() => import("./create/index.jsx"), "discount-card-builder"))
const DiscountCardUpdate = lazy(lazyWithRetry(() => import("./update/index.jsx"), "discount-card-update"))

const EXPORT_OPTIONS = {
  discount_cards: {
    fallbackFilenamePrefix: "discount-cards",
    formats: ["csv", "json", "markdown"],
    label: "Perq Cards",
  },
  special_perq_user_assignments: {
    fallbackFilenamePrefix: "special-perq-user-assignments",
    formats: ["csv"],
    label: "Special Perq User Assignments",
  },
};

const CARD_TYPE_OPTIONS = ["standard", "industry"];
// Stable default: an inline `[]` re-fires the options-cache effect on every render.
const NO_RESTAURANT_GROUPS = [];

export const DiscountCards = () => {
  const [fetchingLoader, setFetchingLoader] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDiscountCard, setSelectedDiscountCard] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [currentSelectedLanguage, setCurrentSelectedLanguage] = useState("");
  const [restaurantGroupId, setRestaurantGroupId] = useState(null);
  const [cardType, setCardType] = useState("");
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEditCard, setSelectedEditCard] = useState({});
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [showSelectedDiscountCard, setShowSelectedDiscountCard] =
    useState(null);
  const [restaurantGroupKey, setRestaurantGroupKey] = useState("");
  const [createInstancePermission, setCreateInstancePermission] =
    useState(false);
  const [updateInstancePermission, setUpdateInstancePermission] =
    useState(false);
  const [deleteInstancePermission, setDeleteInstancePermission] =
    useState(false);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [selectedCardForTranslation, setSelectedCardForTranslation] = useState(null);
  const [isTranslationSaving, setIsTranslationSaving] = useState(false);
  const [translationLoading, setTranslationLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [selectedExportType, setSelectedExportType] = useState('discount_cards')
  const [selectedExportFormat, setSelectedExportFormat] = useState('csv')
  const [exportCardSource, setExportCardSource] = useState(CARD_SOURCE_ALL)
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const createModalTitleRef = useRef(null);
  const editModalTitleRef = useRef(null);
  const restaurantGroupSearchResetTimeoutRef = useRef(null);
  const singleRestaurantGroupAutoApplied = useRef(false)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)

  const { ability } = useContext(AbilityContext);
  const defaultCreatePermission = strictCanScope(
    ability,
    "create_discount_card",
    "Restaurant_Group"
  );

  const showNotification = useNotificationContext();

  const { admin, _loading, _error } = useSelector((state) => state.admin);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const language = mapLanguage(admin?.language_preference);
    setCurrentSelectedLanguage(language);
    i18n.changeLanguage(language);
  }, [admin?.language_preference]);

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = NO_RESTAURANT_GROUPS,
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'))
  }, [restaurantGroups])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroups.length === 1

  useEffect(() => {
    if (filters && restaurantGroupKey) {
      setUpdateInstancePermission(
        strictCanScope(
          ability,
          "update_discount_card",
          "Restaurant_Group",
          `${restaurantGroupKey}`
        )
      );
      setDeleteInstancePermission(
        strictCanScope(
          ability,
          "delete_discount_card",
          "Restaurant_Group",
          `${restaurantGroupKey}`
        )
      );
      setCreateInstancePermission(
        strictCanScope(
          ability,
          "create_discount_card",
          "Restaurant_Group",
          `${restaurantGroupKey}`
        )
      )
    }
  }, [filters, restaurantGroups, ability, restaurantGroupKey]);

  const buildQueryFilter = useCallback((groupId) => ({
    restaurant_group_id: groupId,
    ...(cardType && { card_type: cardType }),
  }), [cardType]);

  useEffect(() => {
    if (restaurantGroupId || filters?.restaurant_group_id || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    const id = restaurantGroups?.[0]?.label
    const queryFilter = buildQueryFilter(id)

    singleRestaurantGroupAutoApplied.current = true
    setRestaurantGroupId(id);
    setFilters(queryFilter);
    setAppliedFilters(queryFilter);
    setRestaurantGroupKey(`${restaurantGroups?.[0]?.label}`)
    setFiltersChanged(false)
  }, [filters, hasSingleAvailableRestaurantGroup, restaurantGroupId, restaurantGroups, buildQueryFilter])

  const fetchDiscountCards = async () => {
    const activeRestaurantGroupId = appliedFilters?.restaurant_group_id
    if (!activeRestaurantGroupId) {
      return [];
    }
    const newFilters = { ...appliedFilters };

    const response = await api.get("/v1/admin/discount_cards", {
      params: {
        per_page: itemsPerPage,
        page: page,
        sort_by: orderBy,
        sort_order: order,
        ...newFilters,
      },
      headers: {
        "Content-Language": currentSelectedLanguage
      }
    },
    );
    return { data: response?.data, pageCount: response?.headers["total-count"] };
  };

  const { data: discountCards, isLoading } = useQuery({
    queryKey: Query_Keys.discount_cards({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      language: currentSelectedLanguage,
      sortBy: orderBy,
      sortOrder: order,
    }),
    queryFn: fetchDiscountCards,
    staleTime: 10 * 60 * 1000,
  });

  const handleCreateClick = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleCreateModalClose = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false);
    setSelectedEditCard({})
  }, []);

  const handleDeleteCard = useCallback((discount_card) => {
    setSelectedDiscountCard(discount_card);
    setDialogOpen(true);
  }, []);

  const handleEditCard = useCallback((discount_card) => {
    setSelectedEditCard(discount_card);
    setEditModalOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const { mutateAsync: deleteDiscountCardMutation } = useOtMutation({
    mutationFn: async (id) => await api.delete(`/v1/admin/discount_cards/${id}`),
    invalidateKeys: [...discountCardMutationKeyGroups.delete, ["associated_guest_restaurants"]],
    successMessage: () => t("discount-cards.delete.success"),
    errorMessage: ({ error }) => error?.response?.data?.errors?.base || "Error",
    onSuccess: () => {
      setSelectedDiscountCard(null);
      resetPageAfterDeletion({ totalCount: discountCards?.pageCount, removed: 1, page, itemsPerPage, setPage })
    },
    onSettled: () => setFetchingLoader(false)
  });

  const handleConfirm = async () => {
    setDialogOpen(false);
    setFetchingLoader(true);
    try {
      await deleteDiscountCardMutation(selectedDiscountCard.id);
    } catch (error) {
      // Errors are surfaced by the mutation hook.
    }
  };

  const handleView = async (discountCard) => {
    let discount_card = await api.get(
      `/v1/admin/discount_cards/${discountCard.id}`, { headers: { "Content-Language": currentSelectedLanguage } }
    );
    return discount_card?.data;
  };

  const handleDiscountCardShow = useCallback((data) => {
    setShowSelectedDiscountCard(data);
    setModalOpen(true);
  }, []);

  const { data: showDiscountCard, isLoading: showDiscountCardLoading } = useQuery({
    queryKey: Query_Keys.show_discount_card({
      discount_card_id: showSelectedDiscountCard?.id,
      language: currentSelectedLanguage,
    }),
    queryFn: () => handleView(showSelectedDiscountCard),
    enabled: Boolean(showSelectedDiscountCard),
    staleTime: 10 * 60 * 1000,
  });
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDiscountCard(null);
  }, []);

  useEffect(() => {
    if (!createModalOpen) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      createModalTitleRef.current?.focus?.({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [createModalOpen]);

  useEffect(() => {
    if (!editModalOpen) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      editModalTitleRef.current?.focus?.({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [editModalOpen]);

  useEffect(() => () => {
    if (restaurantGroupSearchResetTimeoutRef.current) {
      window.clearTimeout(restaurantGroupSearchResetTimeoutRef.current);
    }
  }, []);

  const handleRestaurantGroupSearchClose = useCallback(() => {
    if (restaurantGroupSearchResetTimeoutRef.current) {
      window.clearTimeout(restaurantGroupSearchResetTimeoutRef.current);
    }

    restaurantGroupSearchResetTimeoutRef.current = window.setTimeout(() => {
      setRestaurantGroupSearch('');
      restaurantGroupSearchResetTimeoutRef.current = null;
    }, 1000);
  }, []);

  const handleRestaurantGroupChange = (event) => {
    setRestaurantGroupId(event.target.value);
    setFiltersChanged(true);
    const restaurantGroup = restaurantGroupOptionsCache?.find((data) => data?.label === event.target.value);
    setCreateInstancePermission(
      strictCanScope(
        ability,
        "create_discount_card",
        "Restaurant_Group",
        `${restaurantGroup?.label}`
      )
    );
    setRestaurantGroupKey(restaurantGroup?.label);
  };

  const handleCardTypeChange = (event) => {
    setCardType(event.target.value);
    setFiltersChanged(true);
  };

  const handleCardTypeClear = () => {
    setCardType("");
    setFiltersChanged(true);
  };

  const handleFilterChange = useCallback(() => {
    const queryFilter = buildQueryFilter(restaurantGroupId)
    setFilters(queryFilter);
    setAppliedFilters(queryFilter)
    setItemsPerPage(25);
    setPage(1);
    setFiltersChanged(false);
  }, [restaurantGroupId, buildQueryFilter]);

  const handleOpenExportDialog = () => {
    setSelectedExportType('discount_cards');
    setSelectedExportFormat('csv');
    setExportCardSource(CARD_SOURCE_ALL);
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  const exportExtension = (format) => (format === 'markdown' ? 'md' : format);

  const selectedExportConfig = EXPORT_OPTIONS[selectedExportType];

  const toFilenameSlug = (value) => {
    const text = (value || '').toString().trim().toLowerCase();
    return text
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  };

  const selectedGroupSlug = () => {
    const group = restaurantGroupOptionsCache?.find((item) => String(item?.label) === String(restaurantGroupId));
    return toFilenameSlug(group?.value) || 'group';
  };

  const filenameFromDisposition = (contentDisposition) => {
    if (!contentDisposition) return null;
    const match = contentDisposition.match(/filename="?([^";]+)"?/i);
    return match ? match[1] : null;
  };

  const handleDownloadExport = async () => {
    try {
      setCsvLoading(true);
      const newFilters = {
        ...appliedFilters,
        ...(selectedExportType === 'special_perq_user_assignments' ? cardSourceParams(exportCardSource) : {}),
        export_format: selectedExportFormat,
      };
      const query = buildQueryParams(newFilters);
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=${selectedExportType}&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download export'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      const date = new Date();
      const dispositionFilename = filenameFromDisposition(response.headers.get('content-disposition'));
      const fallbackFilename = `${selectedExportConfig.fallbackFilenamePrefix}-${selectedGroupSlug()}-${dayjs(date).format('YYYY-MM-DD')}.${exportExtension(selectedExportFormat)}`;
      const filename = dispositionFilename || fallbackFilename;

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);
      setExportDialogOpen(false);
      showNotification(`${selectedExportFormat.toUpperCase()} downloaded successfully`, 'success');
    } catch (error) {
      console.error('Export download error:', error);
      showNotification(error?.message || 'Failed to download export', 'error');
    } finally {
      setCsvLoading(false);
    }
  };

  const handleExportTypeChange = (event) => {
    const nextExportType = event.target.value;
    const nextFormats = EXPORT_OPTIONS[nextExportType]?.formats || ['csv'];

    setSelectedExportType(nextExportType);
    if (!nextFormats.includes(selectedExportFormat)) {
      setSelectedExportFormat(nextFormats[0]);
    }
  };

  const getRestaurantGroupName = useCallback((id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => String(res.label) === String(id))
    return restaurantGroup ? restaurantGroup?.value : ''
  }, [restaurantGroupOptionsCache]);

  const dialogBody = () => {
    return (
      <>
        {t("discount-cards.delete.body")}
        <span style={{ fontWeight: "600" }}>
          {` '${selectedDiscountCard?.name}'`}
        </span>
      </>
    );
  };

  const TableColumns = useMemo(() => [
    { key: "id", sortable: true },
    { key: "name_of_the_card", sortable: true },
    { key: "card_type", sortable: true },
    { key: "created_at", sortable: true },
    { key: "updated_at", sortable: true },
    { key: "actions", sortable: false },
    { key: "translate", sortable: false, width: 10 },
  ], []);

  const getSortKeyFromColumnKey = useCallback((columnKey) => {
    switch (columnKey) {
      case "name_of_the_card":
        return "name";
      default:
        return columnKey;
    }
  }, []);

  const handleSortChange = useCallback((columnKey, direction) => {
    setOrderBy(getSortKeyFromColumnKey(columnKey));
    setOrder(direction);
    setPage(1);
  }, [getSortKeyFromColumnKey]);

  const columns = useMemo(() =>
    TableColumns?.map((column) => ({
      key: column.key,
      label: t(`discount-cards.index.table.headers.${camelize(column.key)}`),
      sortable: column.sortable,
      width: column.width
    })), [TableColumns, t]
  );

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        name_of_the_card: {
          display: <DiscountCardName name={record?.name} color={record?.color} />,
          sortValue: record?.name?.toLowerCase()
        },
        card_type: record?.card_type ? t(`discount-cards.index.card-types.${record.card_type}`) : "",
        created_at: formatDate(record?.created_at, record?.time_zone_abbr),
        updated_at: formatDate(record?.updated_at, record?.time_zone_abbr),
        actions: (
          <Box>
            <Tooltip
              title={t("discount-cards.index.table.actions.view")}
              arrow
              onClick={() => handleDiscountCardShow(record)}
            >
              <IconButton>
                <VisibilityIcon
                  sx={{
                    color: "#707070",
                    cursor: "pointer",
                  }}
                />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={t("discount-cards.index.table.actions.edit")}
              arrow
            >
              <span>
                <IconButton disabled={!updateInstancePermission} onClick={() => handleEditCard(record)}>
                  <ModeEditIcon
                    sx={{
                      color: !updateInstancePermission ? "#e0e0e0" : "#707070",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={t("discount-cards.index.table.actions.delete")}
              arrow
            >
              <span>
                <IconButton disabled={!deleteInstancePermission} onClick={() => handleDeleteCard(record)}>
                  <DeleteIcon
                    sx={{
                      color: !deleteInstancePermission ? "#e0e0e0" : "#707070",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
        translate: (
          <React.Fragment>
            {strictCanScope(ability, "update_discount_card", "Restaurant_Group", `${restaurantGroupKey}`) && (
              <Tooltip title="Manage Translations" arrow>
                <IconButton onClick={() => handleOpenTranslationModal(record)}>
                  <TranslateIcon
                    sx={{
                      color: "#707070",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </React.Fragment>
        ),
      };
    });
  };

  const rows = TableRows(discountCards?.data);

  const handleLanguageChange = (lang) => {
    dispatch(updateAdminLanguage(lang));
  };

  const handleOpenTranslationModal = (discountCard) => {
    setSelectedCardForTranslation(discountCard);
    setTranslationModalOpen(true);
  };

  const handleCloseTranslationModal = () => {
    setTranslationModalOpen(false);
    setSelectedCardForTranslation(null);
  };

  const { mutateAsync: updateTranslationMutation } = useOtMutation({
    mutationFn: async (translationData) => await api.put(
        `/v1/admin/discount_cards/${translationData.id}/update_translations`,
        {
          translations: {
            spanish_name: translationData.spanish_name,
            exclusion_items: translationData.exclusion_items,
            rules: translationData.rules,
            discount_card_variants: translationData.discount_card_variants,
          },
        }
    ),
    invalidateKeys: discountCardMutationKeyGroups.update,
    successMessage: () => 'Translation updated successfully',
    errorMessage: ({ error }) => error?.response?.data?.error || 'Error saving translation',
    onSuccess: () => {
      setTranslationModalOpen(false);
      setSelectedCardForTranslation(null);
    },
    onSettled: () => setIsTranslationSaving(false)
  });

  const handleSaveTranslation = async (translationData) => {
    setIsTranslationSaving(true);
    try {
      await updateTranslationMutation(translationData);
    } catch (error) {
      // Errors are surfaced by the mutation hook.
    }
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink 
              LinkComponent={StyledLink} 
              label={t("discount-cards.index.home")} to="/admin/dashboard" 
            />
            <StyledLink color={"#515152"}>
              {t("discount-cards.index.discount-cards")}
            </StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: "18px" }}>
              <React.Fragment>
                <Typography
                  variant="body2"
                  sx={{
                    ...viewTypographyStyles,
                    alignSelf: { xs: "flex-start", sm: "center" },
                    whiteSpace: "nowrap",
                    mt: 0
                  }}
                >
                  {t("discount-cards.index.filters.view")}
                </Typography>
                <FormControl fullWidth size="small">
                  <StyledInputLabel>
                    {t("discount-cards.index.filters.restaurant-group")}
                  </StyledInputLabel>
                  <Select
                    value={restaurantGroupId ? restaurantGroupId : ""}
                    renderValue={() =>
                      getRestaurantGroupName(restaurantGroupId)
                    }
                    size="small"
                    label="Restaurant Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 120,
                      width: 160,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantGroupChange}
                    onClose={handleRestaurantGroupSearchClose}
                    data-testid="discount-card-restaurant-group-select"
                  >
                    <DropdownSearchBar
                      changeSearchText={setRestaurantGroupSearch}
                      placeholder={t(
                        "discount-cards.index.filters.search-restaurant-group"
                      )}
                      isFetching={restaurantGroupFetching}
                    />
                    {
                      restaurantGroups
                        ?.map((res) => (
                          <StyledMenuItem
                            key={res?.label}
                            value={res?.label}
                          >
                            {
                              restaurantGroupId === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupsPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <StyledInputLabel>
                    {t("discount-cards.index.filters.card-type")}
                  </StyledInputLabel>
                  <Select
                    value={cardType}
                    renderValue={(value) => t(`discount-cards.index.card-types.${value}`)}
                    size="small"
                    label={t("discount-cards.index.filters.card-type")}
                    MenuProps={{
                      PaperProps: { sx: { width: 200 } },
                    }}
                    endAdornment={
                      cardType ? (
                        <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                          <IconButton
                            edge="end"
                            size="small"
                            disableRipple
                            onClick={handleCardTypeClear}
                            aria-label={t("discount-cards.index.filters.clear-card-type")}
                          >
                            <CloseIcon sx={{ fontSize: "20px" }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                    sx={{
                      ...selectBaseStyles,
                      minWidth: 120,
                      width: 160,
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleCardTypeChange}
                    data-testid="discount-card-card-type-select"
                  >
                    {CARD_TYPE_OPTIONS.map((option) => (
                      <StyledMenuItem key={option} value={option}>
                        {
                          cardType === option
                            ? <SelectedMenuItem data={t(`discount-cards.index.card-types.${option}`)} />
                            : t(`discount-cards.index.card-types.${option}`)
                        }
                      </StyledMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </React.Fragment>
            </Box>

            <Box sx={{ display: "flex", gap: "18px" }}>
              <UpdateButton
                onClick={handleFilterChange}
                filtersChanged={filtersChanged}
                status={fetchingLoader}
              />

              <LanguageSelector
                value={i18n.language}
                onChange={handleLanguageChange}
                sx={{ ...selectBaseStyles, borderColor: "#2b4fb9" }}
              />
            </Box>
          </Box>
        </FilterContainer>

        <StyledHeadingContainer>
          <Box>
            <StyledTitleContainer>
              <StyledTitle>
                {t("discount-cards.index.mml-discount-programs")}
              </StyledTitle>
            </StyledTitleContainer>
          </Box>
        </StyledHeadingContainer>

        <Divider
          style={{
            borderStyle: "solid",
            borderWidth: "1px",
          }}
        />

        <Box sx={{ padding: "25px 0px" }}>
          <StyledCreateButton
            sx={{ backgroundColor: "#1976d2" }}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCreateClick()}
            disabled={!(defaultCreatePermission || createInstancePermission)}
          >
            {t("discount-cards.index.create-discount-card")}
          </StyledCreateButton>
        </Box>

        <Box>
          { isLoading ? (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "40%",
              }}
            >
              <Loader />
            </Box>
          ) : discountCards?.data && restaurantGroupId ? (
            <div style={{ marginTop: "25px" }}>
              <IndexTable
                columns={columns}
                data={rows}
                tableId="discount-cards"
                type="entries"
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                page={page}
                setPage={setPage}
                pageCount={discountCards?.pageCount}
                report={false}
                showDownloadButton={true}
                disableDownloadButton={discountCards?.data?.length === 0 || csvLoading}
                handleExportCsv={handleOpenExportDialog}
                csvLoading={csvLoading}
                serverSort={true}
                onSortChange={handleSortChange}
                serverOrderBy={TableColumns.find((column) => getSortKeyFromColumnKey(column.key) === orderBy)?.key || ""}
                serverOrder={order}
              />
            </div>
          ) : (
            <div>
              <Alert severity="info">
                {t("discount-cards.index.select-group")}
              </Alert>
            </div>
          )}
        </Box>
        <ConfirmationDialog
          open={dialogOpen}
          onClose={() => handleDialogClose()}
          Icon={CheckIcon}
          dialogTitle={t("discount-cards.delete.title")}
          dialogBody={dialogBody()}
          confirmationText={t("discount-cards.delete.confirmation")}
          handleConfirm={handleConfirm}
        />

        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          data-testid="discount-card-show-modal"
        >
          <ModalContainer>
            <Box sx={{ width: "100%", height: "100%" }}>
              {showDiscountCardLoading ? (
                <LoaderContainer>
                  <Loader />
                </LoaderContainer>
              ) : (
                <React.Fragment>
                  <DiscountCardNameContainer>
                    <StyledTypography variant="h5" sx={{ opacity: '0.8' }}>
                      {showDiscountCard?.name}
                    </StyledTypography>
                    <IconButton onClick={handleModalClose}>
                      <CloseIcon sx={{ color: "#707070" }} />
                    </IconButton>
                  </DiscountCardNameContainer>
                  <DiscountCardContentContainer>
                    <Box sx={{ height: "100%", overflowY: "scroll", width: '100%' }}>
                      <DiscountCardGlobalDetailsView
                        discountCard={showDiscountCard}
                        restaurantGroupName={getRestaurantGroupName(showDiscountCard?.restaurant_group_id)}
                      />
                      <DiscountCardVariantGroupsView
                        t={t}
                        variants={showDiscountCard?.discount_card_variants}
                      />
                    </Box>
                  </DiscountCardContentContainer>
                </React.Fragment>
              )}
            </Box>
          </ModalContainer>
        </Modal>

        <Dialog open={exportDialogOpen} onClose={handleCloseExportDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Export Perq Cards</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Select what you want to download.
            </Typography>
            <RadioGroup
              value={selectedExportType}
              onChange={handleExportTypeChange}
            >
              {Object.entries(EXPORT_OPTIONS).map(([value, config]) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio sx={{ color: '#395bbd', '&.Mui-checked': { color: '#395bbd' } }} />}
                  label={config.label}
                />
              ))}
            </RadioGroup>
            {selectedExportType === 'special_perq_user_assignments' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography id="export-card-source-label" sx={{ mb: 2 }}>
                  Select which cards to include.
                </Typography>
                <RadioGroup
                  aria-labelledby="export-card-source-label"
                  value={exportCardSource}
                  onChange={(event) => setExportCardSource(event.target.value)}
                  data-testid="export-card-source"
                >
                  {CARD_SOURCE_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio sx={{ color: '#395bbd', '&.Mui-checked': { color: '#395bbd' } }} />}
                      label={`${option.label} - ${option.description}`}
                    />
                  ))}
                </RadioGroup>
              </>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ mb: 2 }}>
              Select the format you want to download.
            </Typography>
            <RadioGroup
              value={selectedExportFormat}
              onChange={(event) => setSelectedExportFormat(event.target.value)}
            >
              <FormControlLabel value="csv" control={<Radio sx={{ color: '#395bbd', '&.Mui-checked': { color: '#395bbd' } }} />} label="CSV" />
              {selectedExportConfig.formats.includes("json") && (
                <FormControlLabel value="json" control={<Radio sx={{ color: '#395bbd', '&.Mui-checked': { color: '#395bbd' } }} />} label="JSON" />
              )}
              {selectedExportConfig.formats.includes("markdown") && (
                <FormControlLabel value="markdown" control={<Radio sx={{ color: '#395bbd', '&.Mui-checked': { color: '#395bbd' } }} />} label="Markdown" />
              )}
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseExportDialog} disabled={csvLoading} sx={{ color: '#395bbd' }}>Cancel</Button>
            <Button
              onClick={handleDownloadExport}
              variant="contained"
              disabled={csvLoading}
              sx={{ backgroundColor: '#395bbd', '&:hover': { backgroundColor: '#2f4b9c' } }}
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>

        <EditDiscountCardDialog
          open={createModalOpen}
          onClose={handleCreateModalClose}
          fullWidth
          maxWidth={false}
          scroll="paper"
          data-testid="discount-card-create-modal"
          aria-labelledby="discount-card-create-modal-title"
        >
          <EditDiscountCardDialogHeader>
            <EditDiscountCardDialogTitle
              id="discount-card-create-modal-title"
              ref={createModalTitleRef}
              tabIndex={-1}
            >
              {t("discount-cards.create.create-discount-card")}
            </EditDiscountCardDialogTitle>
            <IconButton
              onClick={handleCreateModalClose}
              aria-label="Close create discount card modal"
            >
              <CloseIcon sx={{ color: "#707070" }} />
            </IconButton>
          </EditDiscountCardDialogHeader>

          <EditDiscountCardDialogContent dividers={false}>
            {createModalOpen && (
              <React.Suspense fallback={<Loader />}>
                <DiscountCardBuilder
                  onClose={handleCreateModalClose}
                />
              </React.Suspense>
            )}
          </EditDiscountCardDialogContent>
        </EditDiscountCardDialog>

        <EditDiscountCardDialog
          open={editModalOpen}
          onClose={handleEditModalClose}
          fullWidth
          maxWidth={false}
          scroll="paper"
          data-testid="discount-card-edit-modal"
          aria-labelledby="discount-card-edit-modal-title"
        >
          <EditDiscountCardDialogHeader>
            <EditDiscountCardDialogTitle
              id="discount-card-edit-modal-title"
              ref={editModalTitleRef}
              tabIndex={-1}
            >
              {t("discount-cards.edit.edit-discount-card")}
            </EditDiscountCardDialogTitle>
            <IconButton
              onClick={handleEditModalClose}
              aria-label="Close edit discount card modal"
            >
              <CloseIcon sx={{ color: "#707070" }} />
            </IconButton>
          </EditDiscountCardDialogHeader>

          <EditDiscountCardDialogContent dividers={false}>
            {Object.keys(selectedEditCard).length > 0 && (
              <React.Suspense fallback={<Loader />}>
                <DiscountCardUpdate
                  key={selectedEditCard?.id}
                  discountCard={selectedEditCard}
                  onClose={handleEditModalClose}
                  restaurantGroups={restaurantGroups}
                />
              </React.Suspense>
            )}
          </EditDiscountCardDialogContent>
        </EditDiscountCardDialog>

        {/* Translation Modal */}
        <DiscountCardTranslationModal
          open={translationModalOpen}
          onClose={handleCloseTranslationModal}
          discountCardData={selectedCardForTranslation}
          restaurantGroupId={restaurantGroupKey}
          onSave={handleSaveTranslation}
          isLoading={isTranslationSaving}
          onLoadingChange={setTranslationLoading}
        />
      </Box>
    </React.Fragment>
  );
};
